import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryConfig } from '@src/lib/api'
import { ListResponseDTO, ResponseDTO } from '@src/types/api'
import {
  CreateLeadRequestDTO,
  Lead,
  LeadActionHistoryCreateDTO,
  LeadActionHistoryResponseDTO,
  LeadContactsChangeDTO, LeadDoublicatePhoneDTO,
  LeadsBaseFilterDTO,
  LeadShortInfo,
  LeadsQueryDTO,
  LeadsQueryFilterDTO
} from '@src/types/leads'
import { dtoToUrlParams } from '@utils/url'

export const leadsApi = createApi({
  reducerPath: 'leadsApi',
  baseQuery: baseQueryConfig('/leads'),
  tagTypes: [ 'ACTION_HISTORY', 'LEADS', 'LEAD' ],
  endpoints: build => ({
    getLeads: build.query<ListResponseDTO<Lead>, LeadsQueryDTO>({
      query: (dto) => {
        const { sort, filter, ...params } = dto
        const urlParams = dtoToUrlParams(params)

        if (sort) {
          urlParams.set('sort', JSON.stringify(sort))
        }

        if (filter) {
          urlParams.set('filter', JSON.stringify(filter))
        }

        return { url: `?${urlParams}` }
      },
      providesTags: (r: ListResponseDTO<Lead> | undefined, error) => ([
        ...((!r || error) ? [] : r.result).map(({ id }) => ({ type: 'LEADS' as const, id })),
        { type: 'LEADS', id: 'LIST' }
      ])
    }),
    getLeadsTotal: build.query<{result: number}, Omit<LeadsQueryFilterDTO, 'sort'>>({
      query: (filter) => {
        const urlParams = dtoToUrlParams(filter)
        return { url: `/total?${urlParams}` }
      },
      providesTags: () => [{ type: 'LEADS', id: 'LIST' }]
    }),
    getLead: build.query<Lead, string>({
      query: (leadId) => ({ url: `/${leadId}` }),
      providesTags: (lead: Lead | undefined, error) => (
        !error && lead ? [{ type: 'LEAD', id: lead.id }] : []
      )
    }),
    exportDealerLeads: build.query<ResponseDTO<Lead[]>, LeadsBaseFilterDTO>({
      query: (filter: LeadsBaseFilterDTO) => ({ url: '/export?' + dtoToUrlParams(filter) }),
    }),
    getLeadActionHistory: build.query<ListResponseDTO<LeadActionHistoryResponseDTO>, string>({
      query: (leadId: string) => ({
        url: `/${leadId}/lead-action-history`
      }),
      providesTags: (result, error, leadId) => ([
        { type: 'ACTION_HISTORY', id: leadId }
      ])
    }),
    addLeadActionHistory: build.mutation<
      ListResponseDTO<LeadActionHistoryResponseDTO>,
      LeadActionHistoryCreateDTO
    >({
      query: ({ leadId, ...body }) => ({
        url: `/${leadId}/lead-action-history`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result, error, { leadId }) => ([
        { type: 'ACTION_HISTORY', id: leadId },
        { type: 'LEADS', id: 'LIST' }
      ])
    }),
    changeLeadContacts: build.mutation<
      ListResponseDTO<LeadActionHistoryResponseDTO>,
      LeadContactsChangeDTO
    >({
      query: ({ leadId, ...body }) => ({
        url: `/${leadId}/change-contacts`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result, error, { leadId }) => ([
        { type: 'LEADS', id: leadId },
        { type: 'LEAD', id: leadId }
      ])
    }),
    searchLeadByINN: build.query<ResponseDTO<LeadShortInfo | null>, string>({
      query: (inn) => ({
        url: `/search-by-inn/${inn}`
      })
    }),
    createLead: build.mutation<
      {
        lead: Lead;
        doublicatePhones: LeadDoublicatePhoneDTO[];
      },
      CreateLeadRequestDTO
    >({
      invalidatesTags: [{ type: 'LEADS', id: 'LIST' }],
      query: (body) => ({
        method: 'POST',
        url: '/',
        body
      }),
    }),
    unPinLead: build.mutation<
      Lead,
      string
    >({
      query: (leadId) => ({
        method: 'POST',
        url: `/${leadId}/un-pin`
      }),
      invalidatesTags: (result, error, leadId) => ([
        { type: 'LEADS', id: leadId },
        { type: 'LEAD', id: leadId }
      ])
    }),
  })
})

export const {
  useLazyGetLeadsQuery,
  useGetLeadActionHistoryQuery,
  useAddLeadActionHistoryMutation,
  useChangeLeadContactsMutation,
  useLazySearchLeadByINNQuery,
  useCreateLeadMutation,
  useLazyGetLeadQuery,
  useGetLeadQuery,
  useLazyGetLeadsTotalQuery,
  useUnPinLeadMutation,
} = leadsApi